// Local store for the fuel register reconciliation.
//
// Three collections, and nothing else:
//
//   declarations   what the reviewer typed for a period — the closing balance of
//                  each tank, or the issued total at a site with no tank, plus
//                  the direct total where it is checked separately.
//   dailyBalances  the same figure day by day, filled in only when the period
//                  does not tally and the reviewer needs to find the day it broke.
//   entryReviews   the verdict reached on one AssetPro entry.
//
// Browser localStorage, in the MongoDB-like shape this codebase already uses
// (src/utils/README_DATABASE.md, analysisDB.js, utilizationDB.js). `_id` is the
// natural key wherever one exists, so a duplicate cannot be created and moving
// this server-side is an insertMany of the exported JSON.
//
// Nothing here writes to AssetPro. A correction records what an entry SHOULD say
// and leaves as a worklist — the AssetPro value is never overwritten, because
// hiding the discrepancy would defeat the reconciliation.

const DB_KEY = 'fuel_register_db_v2';
const DB_VERSION = '2.0';

const COLLECTIONS = ['declarations', 'dailyBalances', 'entryReviews'];

const now = () => new Date().toISOString();
const rand = () => Math.random().toString(36).slice(2, 11);

const emptyDB = () => {
  const db = { _version: DB_VERSION, _createdAt: now(), _updatedAt: now() };
  COLLECTIONS.forEach((c) => { db[c] = []; });
  return db;
};

/** A corrupt store is reported and treated as EMPTY, never silently "repaired". */
export const loadDB = () => {
  try {
    const raw = localStorage.getItem(DB_KEY);
    if (!raw) return emptyDB();
    const parsed = JSON.parse(raw);
    const db = { ...emptyDB(), ...parsed };
    COLLECTIONS.forEach((c) => { db[c] = Array.isArray(parsed[c]) ? parsed[c] : []; });
    return db;
  } catch (e) {
    console.error('[registerDB] load failed — treating the store as empty:', e);
    return emptyDB();
  }
};

const saveDB = (db) => {
  const next = { ...db, _version: DB_VERSION, _updatedAt: now() };
  try {
    localStorage.setItem(DB_KEY, JSON.stringify(next));
  } catch (e) {
    // The caller must know the write did NOT happen — silently dropping register
    // figures would be the worst possible failure here.
    console.error('[registerDB] save failed:', e);
    throw new Error(`Register data could not be saved: ${e.message}`);
  }
  return next;
};

const upsertInto = (list, doc) => {
  const i = list.findIndex((d) => d._id === doc._id);
  const stamped = {
    ...doc,
    _createdAt: (i >= 0 && list[i]._createdAt) || doc._createdAt || now(),
    _updatedAt: now(),
  };
  if (i >= 0) list[i] = stamped;
  else list.push(stamped);
  return stamped;
};

// ---------------------------------------------------------------------------
// Declarations — one per period + scope
// ---------------------------------------------------------------------------
//
// { _id, periodKey, scopeKey,
//   issuedLtr,          // sites with no tank
//   directLtr,          // multi-tank sites, checked separately
//   tanks: { <storageId>: closingLtr },
//   enteredBy, enteredAt, note }

export const declarationIdOf = (periodKey, scopeKey) => `rdc_${periodKey}_${scopeKey}`;

export const getDeclaration = (periodKey, scopeKey) =>
  loadDB().declarations.find((d) => d._id === declarationIdOf(periodKey, scopeKey)) || null;

export const upsertDeclaration = ({ periodKey, scopeKey, ...rest }) => {
  if (!periodKey || !scopeKey) throw new Error('A declaration needs a period and a scope.');
  const db = loadDB();
  const existing = db.declarations.find((d) => d._id === declarationIdOf(periodKey, scopeKey));
  const doc = {
    issuedLtr: null,
    directLtr: null,
    tanks: {},
    note: null,
    ...(existing || {}),
    ...rest,
    _id: declarationIdOf(periodKey, scopeKey),
    periodKey,
    scopeKey,
    enteredBy: 'Current User',
    enteredAt: now(),
  };
  const saved = upsertInto(db.declarations, doc);
  saveDB(db);
  return saved;
};

/**
 * Set ONE target's period figure without disturbing the others.
 *
 * The period closing and the last day's closing are the same number seen from two
 * screens, so editing either has to move both. A plain upsertDeclaration would
 * replace the whole `tanks` map and wipe the other tanks.
 */
export const setDeclarationFigure = ({ periodKey, scopeKey, target, valueLtr }) => {
  const existing = getDeclaration(periodKey, scopeKey) || {};
  if (target === 'ISSUED') {
    return upsertDeclaration({ periodKey, scopeKey, issuedLtr: valueLtr });
  }
  if (target === 'DIRECT') {
    return upsertDeclaration({ periodKey, scopeKey, directLtr: valueLtr });
  }
  return upsertDeclaration({
    periodKey,
    scopeKey,
    tanks: { ...(existing.tanks || {}), [target]: valueLtr },
  });
};

// ---------------------------------------------------------------------------
// Daily balances — one figure per day, per target
// ---------------------------------------------------------------------------
//
// `target` is a storage id for a tank's closing balance, or 'ISSUED' at a site
// with no tank. One number a day is all that is asked for.
//
// { _id, periodKey, scopeKey, target, date, valueLtr, note }

export const dailyIdOf = (periodKey, target, date) =>
  `rdb_${periodKey}_${target}_${String(date).replace(/-/g, '')}`;

export const listDailyBalances = ({ periodKey, target } = {}) => {
  const db = loadDB();
  return db.dailyBalances
    .filter((d) => (periodKey ? d.periodKey === periodKey : true))
    .filter((d) => (target ? d.target === target : true))
    .sort((a, b) => (a.date < b.date ? -1 : 1));
};

export const upsertDailyBalance = ({ periodKey, scopeKey, target, date, valueLtr, note }) => {
  if (!periodKey || !target || !date) throw new Error('A daily balance needs a period, a target and a date.');
  const db = loadDB();
  // Clearing the box removes the row entirely, so the day reads as MISSING
  // rather than as a stored zero.
  if (valueLtr === null || valueLtr === undefined) {
    db.dailyBalances = db.dailyBalances.filter((d) => d._id !== dailyIdOf(periodKey, target, date));
    saveDB(db);
    return null;
  }
  const doc = {
    _id: dailyIdOf(periodKey, target, date),
    periodKey,
    scopeKey,
    target,
    date,
    valueLtr,
    note: note || null,
    enteredBy: 'Current User',
    enteredAt: now(),
  };
  const saved = upsertInto(db.dailyBalances, doc);
  saveDB(db);
  return saved;
};

// ---------------------------------------------------------------------------
// Entry reviews
// ---------------------------------------------------------------------------
//
// { _id, periodKey, entryKey, entryId, kind, storageId, date, assetCode,
//   quantityL, verdict, correctQuantityL, correctDate, note,
//   reviewedBy, reviewedAt, actioned }

export const entryReviewIdOf = (periodKey, entryKey) => `rer_${periodKey}_${entryKey}`;

export const listEntryReviews = ({ periodKey } = {}) => {
  const db = loadDB();
  return db.entryReviews.filter((r) => (periodKey ? r.periodKey === periodKey : true));
};

export const upsertEntryReview = ({ periodKey, entryKey, ...rest }) => {
  if (!periodKey || !entryKey) throw new Error('A review needs a period and an entry.');
  const db = loadDB();
  const doc = {
    correctQuantityL: null,
    correctDate: null,
    // Which tank a mis-sourced direct fill actually came out of.
    correctStorageId: null,
    correctStorageCode: null,
    // Which machine the fuel actually went to, when only the asset code is wrong.
    correctAssetId: null,
    correctAssetCode: null,
    note: null,
    actioned: false,
    ...rest,
    _id: entryReviewIdOf(periodKey, entryKey),
    periodKey,
    entryKey,
    reviewedBy: 'Current User',
    reviewedAt: now(),
  };
  const saved = upsertInto(db.entryReviews, doc);
  saveDB(db);
  return saved;
};

export const clearEntryReview = (periodKey, entryKey) => {
  const db = loadDB();
  db.entryReviews = db.entryReviews.filter((r) => r._id !== entryReviewIdOf(periodKey, entryKey));
  saveDB(db);
};

/**
 * A movement the register records that AssetPro never did. It has no AssetPro
 * entry behind it, so it is stored without an entryKey and carries its own id.
 */
export const addMissingFromAssetPro = ({ periodKey, scopeKey, storageId, date, assetCode, quantityL, kind, note }) => {
  const db = loadDB();
  const doc = {
    _id: `rmi_${Date.now()}_${rand()}`,
    periodKey,
    scopeKey,
    entryKey: null,
    storageId: storageId || null,
    date,
    assetCode: assetCode || null,
    quantityL,
    kind: kind || 'ISSUE',
    verdict: 'MISSING_IN_ASSETPRO',
    note: note || null,
    reviewedBy: 'Current User',
    reviewedAt: now(),
    actioned: false,
  };
  db.entryReviews.push({ ...doc, _createdAt: now(), _updatedAt: now() });
  saveDB(db);
  return doc;
};

export const deleteEntryReviewById = (id) => {
  const db = loadDB();
  db.entryReviews = db.entryReviews.filter((r) => r._id !== id);
  saveDB(db);
};

// ---------------------------------------------------------------------------
// Whole-store operations
// ---------------------------------------------------------------------------

export const getStats = () => {
  const db = loadDB();
  const s = { updatedAt: db._updatedAt };
  COLLECTIONS.forEach((c) => { s[c] = db[c].length; });
  return s;
};

export const exportDB = () => {
  const db = loadDB();
  const blob = new Blob([JSON.stringify(db, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fuel-register-${new Date().toISOString().slice(0, 10)}.json`;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return true;
};

/** Merge by `_id`, so importing a colleague's file adds to yours rather than replacing it. */
export const importDB = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target.result);
        const known = COLLECTIONS.filter((c) => Array.isArray(imported[c]));
        if (!known.length) {
          reject(new Error('This file does not look like a fuel register export.'));
          return;
        }
        const db = loadDB();
        const counts = {};
        known.forEach((c) => {
          counts[c] = 0;
          imported[c].forEach((doc) => {
            if (!doc || !doc._id) return;
            upsertInto(db[c], doc);
            counts[c] += 1;
          });
        });
        saveDB(db);
        resolve(counts);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.readAsText(file);
  });

export const clearDB = () => {
  localStorage.removeItem(DB_KEY);
  return true;
};

// MONGO INDEXES, for the eventual move server-side:
//   declarations  { periodKey: 1, scopeKey: 1 } unique
//   dailyBalances { periodKey: 1, target: 1, date: 1 } unique
//   entryReviews  { periodKey: 1, entryKey: 1 } unique, { verdict: 1 }
// The natural keys are already the _ids, so migration is an insertMany.
